import { useState, useEffect, useCallback } from "react";

const STOCK_REPORT_KEY = "stock_report";
const STRATEGY_REPORT_KEY = "strategy_report";
const SETTINGS_REPORT_KEY = "settings_report";

/**
 * Get the entire report object from localStorage
 */
const getReportObject = (
  storageKey: string,
): Record<string, number | string> => {
  try {
    const item = window.localStorage.getItem(storageKey);
    if (item === null) return {};
    return JSON.parse(item);
  } catch (error) {
    console.warn(`Error reading localStorage key "${storageKey}":`, error);
    return {};
  }
};

/**
 * Set a value in the report object in localStorage
 */
const setReportValue = (
  storageKey: string,
  key: string,
  value: number | string,
): void => {
  try {
    const report = getReportObject(storageKey);
    report[key] = value;
    window.localStorage.setItem(storageKey, JSON.stringify(report));

    // Dispatch custom event to sync across components
    window.dispatchEvent(
      new CustomEvent("reportStorageChange", {
        detail: { storageKey, key, value },
      }),
    );
  } catch (error) {
    console.error(`Error setting localStorage key "${storageKey}":`, error);
  }
};

/**
 * Hook to manage stock report values in localStorage
 * @param key - The key within the stock_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useStockReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const [storedValue, setStoredValue] = useState<number | string>(() => {
    if (!key) return defaultValue;

    const report = getReportObject(STOCK_REPORT_KEY);
    return report[key] !== undefined ? report[key] : defaultValue;
  });

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: number | string) => {
      if (!key) return;

      setStoredValue(value);
      setReportValue(STOCK_REPORT_KEY, key, value);
    },
    [key],
  );

  // Listen for changes from other components
  useEffect(() => {
    if (!key) return;

    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        storageKey: string;
        key: string;
        value: number | string;
      }>;
      if (
        customEvent.detail.storageKey === STOCK_REPORT_KEY &&
        customEvent.detail.key === key
      ) {
        setStoredValue(customEvent.detail.value);
      }
    };

    window.addEventListener("reportStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("reportStorageChange", handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue];
};

/**
 * Hook to manage strategy report values in localStorage
 * @param key - The key within the strategy_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useStrategyReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const [storedValue, setStoredValue] = useState<number | string>(() => {
    if (!key) return defaultValue;

    const report = getReportObject(STRATEGY_REPORT_KEY);
    return report[key] !== undefined ? report[key] : defaultValue;
  });

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: number | string) => {
      if (!key) return;

      setStoredValue(value);
      setReportValue(STRATEGY_REPORT_KEY, key, value);
    },
    [key],
  );

  // Listen for changes from other components
  useEffect(() => {
    if (!key) return;

    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        storageKey: string;
        key: string;
        value: number | string;
      }>;
      if (
        customEvent.detail.storageKey === STRATEGY_REPORT_KEY &&
        customEvent.detail.key === key
      ) {
        setStoredValue(customEvent.detail.value);
      }
    };

    window.addEventListener("reportStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("reportStorageChange", handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue];
};

/**
 * Hook to manage settings report values in localStorage
 * @param key - The key within the settings_report object
 * @param defaultValue - Default value if key doesn't exist
 * @returns [value, setValue] - Current value and setter function
 */
export const useSettingsReportValue = (
  key: string,
  defaultValue: number | string,
): [number | string, (value: number | string) => void] => {
  const [storedValue, setStoredValue] = useState<number | string>(() => {
    if (!key) return defaultValue;

    const report = getReportObject(SETTINGS_REPORT_KEY);
    return report[key] !== undefined ? report[key] : defaultValue;
  });

  // Update localStorage when value changes
  const setValue = useCallback(
    (value: number | string) => {
      if (!key) return;

      setStoredValue(value);
      setReportValue(SETTINGS_REPORT_KEY, key, value);
    },
    [key],
  );

  // Listen for changes from other components
  useEffect(() => {
    if (!key) return;

    const handleStorageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        storageKey: string;
        key: string;
        value: number | string;
      }>;
      if (
        customEvent.detail.storageKey === SETTINGS_REPORT_KEY &&
        customEvent.detail.key === key
      ) {
        setStoredValue(customEvent.detail.value);
      }
    };

    window.addEventListener("reportStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("reportStorageChange", handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue];
};
